/**
 * Reference data the console pickers need: crops, villages (groups), people (users), orgs (labs),
 * and media (photo records / evidence upload).
 *   GET /crops · GET /groups · GET /groups/:id · GET /users?q · GET /orgs?kind · GET /media/:id · PUT /media/:id
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { compressImage } from '@/lib/image'
import { newId } from '@/lib/ids'
import { getList, getOne, getPage, type Page } from './core'
import { ck } from './keys'
import { arr, obj, personName, str, strOrNull } from './mappers'

export interface Option {
  id: string
  name: string
  hint?: string | null
}

export interface VillageOption extends Option {
  stateName: string | null
  stateCode: string | null
}

const mapCrop = (r: Record<string, unknown>): Option => ({ id: str(r.id), name: str(r.name) })
const mapGroup = (r: Record<string, unknown>): VillageOption => {
  const state = obj(r.state)
  const stateName = strOrNull(state.name)
  return {
    id: str(r.id),
    name: str(r.name),
    hint: stateName ?? strOrNull(r.district),
    stateName,
    stateCode: strOrNull(state.code),
  }
}

export interface UserOption extends Option {
  username: string
  farmerCode: string | null
  contactNo: string | null
  roles: string[]
}
export const mapUser = (r: Record<string, unknown>): UserOption => ({
  id: str(r.id),
  name: personName(r) ?? str(r.username),
  username: str(r.username),
  farmerCode: strOrNull(r.farmerCode ?? r.farmer_code),
  contactNo: strOrNull(r.contactNo ?? r.contact_no),
  roles: arr(r.roles).map((x) => str(typeof x === 'string' ? x : obj(x).name)),
  hint: strOrNull(r.farmerCode ?? r.farmer_code) ?? strOrNull(r.email),
})

const STATIC = { staleTime: 10 * 60_000 }

export function useCrops() {
  return useQuery({
    queryKey: [...ck.reference, 'crops'],
    queryFn: () => getPage('/crops', { page: 1, limit: 100 }, mapCrop).then((p) => p.rows),
    ...STATIC,
  })
}

export function useVillages() {
  return useQuery({
    queryKey: [...ck.reference, 'groups'],
    queryFn: () => getPage('/groups', { page: 1, limit: 200 }, mapGroup).then((p) => p.rows),
    ...STATIC,
  })
}

export interface VillageDetail extends Option {
  members: UserOption[]
  supervisors: UserOption[]
}
/** GET /groups/:id — members and supervisors (the village roster print, F4.2-i). */
export function useVillage(id: string | null) {
  return useQuery({
    queryKey: [...ck.reference, 'group', id],
    enabled: Boolean(id),
    queryFn: () =>
      getOne(`/groups/${id}`, (r): VillageDetail => {
        const farmsOwners = arr(r.farms)
          .map((f) => obj(obj(obj(f).farm).owner))
          .filter((o) => o.id)
        const members = [...arr(r.members).map((m) => obj(obj(m).user)), ...farmsOwners]
          .filter((u) => u.id)
          .map(mapUser)
        const seen = new Set<string>()
        return {
          ...mapGroup(r),
          members: members.filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true))),
          supervisors: arr(r.supervisors)
            .map((s) => obj(obj(s).user))
            .filter((u) => u.id)
            .map(mapUser),
        }
      }),
  })
}

/** GET /users?q= — people search (farmers, operators, finance). */
export function searchUsers(q: string, limit = 20): Promise<Page<UserOption>> {
  return getPage('/users', { q: q.trim() || undefined, page: 1, limit }, mapUser)
}
export function useUserSearch(q: string, enabled = true) {
  return useQuery({
    queryKey: [...ck.reference, 'users', q],
    queryFn: () => searchUsers(q),
    enabled,
    staleTime: 60_000,
  })
}
export function useUser(id: string | null) {
  return useQuery({
    queryKey: [...ck.reference, 'user', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/users/${id}`, mapUser),
    staleTime: 5 * 60_000,
  })
}

export interface OrgOption extends Option {
  kind: string
  code: string | null
}
export function useOrgs(kind?: string) {
  return useQuery({
    queryKey: [...ck.reference, 'orgs', kind ?? 'all'],
    queryFn: () =>
      getList('/orgs', { kind }, (r): OrgOption => ({
        id: str(r.id),
        name: str(r.name),
        kind: str(r.kind),
        code: strOrNull(r.code),
      })),
    ...STATIC,
  })
}

// ---------------- media ----------------

export interface MediaRecord {
  id: string
  /** A signed link that works for about an hour (photos are not public). */
  url: string | null
  urlExpiresAt: string | null
  contentType: string | null
}

const isUrl = (v: string) => /^(https?:|blob:|data:|\/)/.test(v)

/** Re-read a photo record well before its signed link runs out. */
export const MEDIA_REFRESH_MS = 40 * 60_000

export const mapMediaRecord = (r: Record<string, unknown>): MediaRecord => ({
  id: str(r.id),
  url: strOrNull(r.url),
  urlExpiresAt: strOrNull(r.urlExpiresAt),
  contentType: strOrNull(r.contentType),
})

/**
 * A photo reference from the API is a media id (phone UUIDv7) or already a URL. A media id is
 * read through GET /media/:id, which answers with a short-lived signed link; it is re-read before
 * the link expires, and `refresh` gets a new one (e.g. when an image fails to load).
 */
export function useMediaUrl(ref: string | null | undefined) {
  const direct = ref && isUrl(ref) ? ref : null
  const q = useQuery({
    queryKey: [...ck.media, ref],
    enabled: Boolean(ref) && !direct,
    retry: false,
    staleTime: MEDIA_REFRESH_MS / 2,
    refetchInterval: MEDIA_REFRESH_MS,
    queryFn: () => getOne(`/media/${ref}`, mapMediaRecord),
  })
  return {
    url: direct ?? q.data?.url ?? null,
    loading: !direct && q.isLoading,
    /** 404 = the phone has not uploaded it yet. */
    missing: !direct && q.isError,
    refresh: () => (direct ? undefined : void q.refetch()),
  }
}

/** Compress and upload an evidence photo with a fresh UUIDv7 (PUT /media/:id). Returns the id. */
export async function uploadEvidence(file: Blob): Promise<string> {
  const id = newId()
  const blob = file.type.startsWith('image/') ? await compressImage(file, { maxEdge: 2000 }) : file
  await api.put(`/media/${id}`, blob, { timeoutMs: 60_000 })
  return id
}
